<template>
  <div v-if="isActive" class="preview-overlay preview-background" @click="handleOverlayClick">
    <div class="preview-container d-flex justify-content-center align-items-center preview-background">
      <!-- Dark blur background -->
      <div class="blur-background position-fixed top-0 start-0 w-100 h-100 preview-background"></div>

      <!-- Image container -->
      <div class="position-relative d-flex justify-content-center align-items-center w-100 h-100 preview-background">
        <img v-if="imageUrl"
             ref="imageRef"
             :src="imageUrl"
             alt="Generated image"
             class="preview-image"
             :style="{
            transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
          }"
             @wheel.prevent="handleWheel"
             @mousedown="startPan"
             @mousemove="updatePan"
             @mouseup="endPan"
             @mouseleave="endPan"
             @click="handleClick"
        />
        <div v-else class="preview-placeholder">
          <h2>Create your first image</h2>
        </div>


        <!-- Toolbar -->
        <div v-if="imageUrl && !inSlideshow"
             class="previews-button-bar position-absolute bottom-0 start-50 translate-middle-x mb-2 px-3 py-2 rounded-pill d-flex gap-2 align-items-center"
             @click.stop>

          <button
              class="btn btn-sm btn-outline-primary px-2 py-1 btn-download"
              @click="isActive = false"
          >
            Close
          </button>

          <a
              class="btn btn-sm btn-outline-primary px-2 py-1 btn-download"
              :href="imageUrl + '?download'"
              target="_blank"
          >
            <font-awesome-icon :icon="['fas', 'arrow-down-to-line']" />
          </a>
          <button
              class="btn btn-sm btn-outline-primary px-2 py-1 btn-download me-3"
              @click="slideShow"
          >
            <font-awesome-icon :icon="['fas', 'presentation-screen']" />
          </button>

          <button
              class="btn btn-sm btn-outline-danger px-2 py-1 btn-download"
              @click="remove"
          >
            <font-awesome-icon :icon="['fal', 'trash-alt']" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, watch} from 'vue'
import {GALLERY_ENDPOINT} from "@/services/endpoints";
import {addIcon} from "@/plugins/fontawesome";
import {
  faArrowDownToLine,
    faPresentationScreen
} from '@fortawesome/pro-solid-svg-icons';
import dialogManager from "@/components/dialogs/dialogManager";
addIcon([faArrowDownToLine, faPresentationScreen])

const props = defineProps({
  currentImage: {
    type: String,
    default: null
  },
  imageMetadata: {
    default: null
  },
  active: {
    type: Boolean,
    default: false
  },
  hasPrev: {
    type: Boolean,
    default: false
  },
  hasNext: {
    type: Boolean,
    default: false
  }
})

watch(() => props.currentImage, (newValue, oldValue) => {
  imageUrl.value = newValue;
  advanceMode.value = false;
});

watch(() => props.imageMetadata, (newValue, oldValue) => {
  imageUrl.value = GALLERY_ENDPOINT + "/" + newValue.path;
  advanceMode.value = true;
});

const emit = defineEmits(['remove', 'prev', 'next', 'slideshow-next', 'update:active'])

// Zoom and pan functionality
const zoom = ref(1)
const panX = ref(0)
const panY = ref(0)
const isPanning = ref(false)
const startX = ref(0)
const startY = ref(0)
const imageRef = ref(null)
const isActive = ref(false)
const inSlideshow = ref(false)

const imageUrl = ref(props.imageMetadata != null ? props.imageMetadata.path : props.currentImage)
const advanceMode = ref(props.imageMetadata != null)


const MIN_ZOOM = 1
const MAX_ZOOM = 4
const ZOOM_SPEED = 0.1


function show(){
  isActive.value = true;
  inSlideshow.value = false;
}

function handleClick(event) {
  if(inSlideshow.value){
    inSlideshow.value = false;
    return;
  }

  // Get click position relative to the image
  const rect = event.target.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const clickPosition = x / rect.width; // Get relative position (0 to 1)

  // Define zones (20% from each side)
  const leftZone = 0.5;
  const rightZone = 0.5;

  if (clickPosition <= leftZone) {
    emit('prev');
  } else if (clickPosition >= rightZone) {
    emit('next');
  }
}

let timer = null;

function slideshowTick(){
  timer = null;
  if(!inSlideshow.value){
    return;
  }
  timer = setTimeout(slideshowTick, 3000);

  emit('slideshow-next');
}

async function slideShow(){
  if(!advanceMode.value){
    await dialogManager.createAccountAd("Account required", "Create a free account to unlock the slideshow");
    return;
  }
  inSlideshow.value = true;
  if(timer){
    clearTimeout(timer);
  }

  timer = setTimeout(slideshowTick, 3000);
}

function handleWheel(event) {
  // const rect = event.target.getBoundingClientRect()
  // const mouseX = event.clientX - rect.left
  // const mouseY = event.clientY - rect.top
  //
  // // Calculate zoom
  // const delta = -Math.sign(event.deltaY) * ZOOM_SPEED
  // const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom.value + delta))
  //
  // if (newZoom !== zoom.value) {
  //   // Adjust pan to keep mouse position stable
  //   const scale = newZoom / zoom.value
  //   panX.value = mouseX - (mouseX - panX.value) * scale
  //   panY.value = mouseY - (mouseY - panY.value) * scale
  //   zoom.value = newZoom
  // }
}

function startPan(event) {
  // isPanning.value = true
  // startX.value = event.clientX - panX.value
  // startY.value = event.clientY - panY.value
}

function updatePan(event) {
  // if (isPanning.value) {
  //   panX.value = event.clientX - startX.value
  //   panY.value = event.clientY - startY.value
  // }
}

function endPan() {
  // isPanning.value = false
}

function handleOverlayClick(event) {
  if (event.target.classList.contains('preview-background')) {
    isActive.value = false;
    inSlideshow.value = false;
    emit('update:active', false)
  }
}

// Keyboard controls
function handleKeydown(event) {
  if (!isActive.value) return

  switch(event.key) {
    case 'ArrowLeft':
      if (props.hasPrev) emit('prev')
      break
    case 'ArrowRight':
    case ' ':
      if (props.hasNext) emit('next')
      break
    case 'Escape':
      emit('update:active', false);
      if(inSlideshow.value){
        inSlideshow.value = false;
      } else {
        isActive.value = false
      }
      break
  }

  // Handle zoom with Ctrl+ and Ctrl-
  if (event.ctrlKey) {
    if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      zoom.value = Math.min(MAX_ZOOM, zoom.value + ZOOM_SPEED)
    } else if (event.key === '-') {
      event.preventDefault()
      zoom.value = Math.max(MIN_ZOOM, zoom.value - ZOOM_SPEED)
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const remove = async () => {
  if(await dialogManager.confirmation("Removing picture", "Do you like to delete selected picture")){
    emit('remove')
  }
}

defineExpose({
  show
})
</script>

<style scoped>
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1050;
}

.blur-background {
  background-color: rgba(33, 37, 41, 0.85);
  backdrop-filter: blur(8px);
}

.preview-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.preview-image {
  max-height: 95vh;
  max-width: 95vw;
  object-fit: contain;
  transition: transform 0.1s ease-out;
  cursor: grab;
}

.preview-image:active {
  cursor: grabbing;
}

.previews-button-bar {
  background-color: rgba(33, 37, 41, 0.95);
  backdrop-filter: blur(8px);
  color: white;
  z-index: 1060;
}

.nav-area {
  position: absolute;
  top: 0;
  height: 100%;
  width: 20%;
  cursor: pointer;
}

.nav-prev {
  left: 0;
}

.nav-next {
  right: 0;
}

.nav-area:hover {
  cursor: pointer;
}
</style>